import React, {
  useRef, useEffect, useContext,
} from 'react';
import {
  Keyboard, Animated, Platform, ScrollView,
} from 'react-native';
import { Formik } from 'formik';
import {
  FormControl, NativeBaseProvider,
} from 'native-base';
import {
  Button, Dialog, Portal, Text, TextInput,
} from 'react-native-paper';
import { DatePickerInput } from 'react-native-paper-dates';
import { useMutation } from '@apollo/client';
import { styles } from '../../screens/Common.style';
import { ModalContext } from '../../context/ModalContext';
import { CreateAnnouncement } from '../../gql/announcement/mutation';
import { announcementSchema } from '../../utils/yupSchema';
import { FindMyAnnouncements } from '../../gql/announcement/query';

function Announcement() {
  const {
    displayCreateAnnouncementModal,
    toggleCreateAnnouncementModal,
  } = useContext(ModalContext);
  const [createAnnouncement] = useMutation(CreateAnnouncement);
  const shift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return () => {};
    }
    const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
      Animated.timing(shift, {
        toValue: -(e.endCoordinates.height / 2),
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    const keyboardWillHide = Keyboard.addListener('keyboardWillHide', (e) => {
      Animated.timing(shift, {
        toValue: 0,
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, [shift]);

  return (
    <Formik
      initialValues={{
        title: '',
        message: '',
        startDate: new Date(Date.now()),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      }}
      validateOnChange
      validationSchema={announcementSchema}
      onSubmit={async (values) => {
        await createAnnouncement({
          variables: {
            title: values.title,
            message: values.message,
            startDate: values.startDate,
            endDate: values.endDate,
          },
          refetchQueries: [
            FindMyAnnouncements, // DocumentNode object parsed with gql
            'findMyAnnouncements', // Query name
          ],
        });
        toggleCreateAnnouncementModal();
      }}
    >
      {({
        handleSubmit, values, errors, setFieldValue, isValid,
      }) => (
        <Animated.View style={[{ flex: 1, transform: [{ translateY: shift }] }]}>
          <Dialog
            visible={displayCreateAnnouncementModal}
            dismissable
            onDismiss={toggleCreateAnnouncementModal}
          >
            <ScrollView>
              <Dialog.Title>Faire une annonce</Dialog.Title>
              <Dialog.Content>
                <FormControl style={styles.field}>
                  <FormControl.Label>Titre</FormControl.Label>
                  <TextInput
                    style={styles.whiteBackground}
                    onChangeText={(value) => setFieldValue('title', value)}
                    value={values.title}
                    mode="outlined"
                    label="Titre"
                  />
                </FormControl>
                {!!errors.title && <Text style={styles.error}>{errors.title}</Text>}
                <FormControl style={styles.field}>
                  <FormControl.Label>Message</FormControl.Label>
                  <TextInput
                    style={styles.whiteBackground}
                    onChangeText={(value) => setFieldValue('message', value)}
                    value={values.message}
                    multiline
                    mode="outlined"
                    label="Message"
                  />
                </FormControl>
                {!!errors.message && <Text style={styles.error}>{errors.message}</Text>}
                <FormControl style={styles.field}>
                  <FormControl.Label>Valable du</FormControl.Label>
                  <DatePickerInput
                    presentationStyle="pageSheet"
                    style={styles.whiteBackground}
                    locale="fr-FR"
                    label="Début"
                    value={values.startDate}
                    onChange={(d) => {
                      d.setHours(d.getHours() + 12);
                      if (d !== new Date(values.startDate)) setFieldValue('startDate', d);
                    }}
                    inputMode="start"
                    mode="flat"
                  />
                </FormControl>
                {!!errors.startDate && <Text style={styles.error}>{errors.startDate}</Text>}
                <FormControl style={styles.field}>
                  <FormControl.Label>Jusqu'à</FormControl.Label>
                  <DatePickerInput
                    presentationStyle="pageSheet"
                    style={styles.whiteBackground}
                    locale="fr-FR"
                    label="Fin"
                    value={values.endDate}
                    onChange={(d) => {
                      d.setHours(d.getHours() + 12);
                      if (d !== new Date(values.startDate)) setFieldValue('endDate', d);
                    }}
                    inputMode="start"
                    mode="flat"
                  />
                </FormControl>
                {!!errors.endDate && <Text style={styles.error}>{errors.endDate}</Text>}
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={toggleCreateAnnouncementModal}>
                  Annuler
                </Button>
                <Button disabled={!isValid} onPress={handleSubmit}>
                  Envoyer
                </Button>
              </Dialog.Actions>
            </ScrollView>
          </Dialog>
        </Animated.View>
      )}
    </Formik>
  );
}

export default function AnnouncementModal() {
  return (
    <Portal>
      <NativeBaseProvider>
        <Announcement />
      </NativeBaseProvider>
    </Portal>
  );
}
